/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./PageCollectionView',
	'jquery',
	'underscore',
	'react',
	'react-dom',
	'corsair_ship/ba-react-html5-dnd',
	'ca-ui-toolkit',
	'../../util/ColorUtils',
	'../../../app/nls/StringResources',
	'../../widgets/PropertiesUtil',
	'../../../app/ui/dialogs/ConfirmationDialog',
	'../../../app/util/EventChainLocal',
	'../../../lib/@waca/core-client/js/core-client/utils/BidiUtil'
], function(BaseLayout, $, _, React, ReactDOM, ReactHtml5DnDContext, UI_Toolkit, ColorUtils, StringResources, PropertiesUtil, ConfirmationDialog, EventChainLocal, BidiUtil) {
	/*global MutationObserver*/

	const linePositionMap = {
		top: 'bottom',
		left: 'right',
		bottom: 'top',
		right: 'left'
	};

	const colorPropertiesMapping = [{
		modelPropertyName: 'tabTextColor',
		toolkitPropertyName: 'textColor'
	}, {
		modelPropertyName: 'tabSelectedLineColor',
		toolkitPropertyName: 'lineColor'
	}, {
		modelPropertyName: 'tabBackgroundColor',
		toolkitPropertyName: 'backgroundColor'
	}, {
		modelPropertyName: 'tabIconColor',
		toolkitPropertyName: 'iconColor'
	}];

	let Tab = null;
	Tab = BaseLayout.extend({

		init: function(options) {
			Tab.inherited('init', this, arguments);

			this.eventRouter = options.eventRouter;
			this.canvas = options.canvas;
			this.colorsService = this.dashboardApi.getFeature('Colors');
			this._icons = this.dashboardApi.getFeature('Icons');
			this._dashboardState = this.dashboardApi.getFeature('DashboardState');

			this._registerEvents();

			this.initialTab = this.model.items[0];
			if (options.appSettings && options.appSettings.subView) {
				_.each(this.model.items, function(item) {
					if (item.id === options.appSettings.subView) {
						this.initialTab = item;
					}
				}.bind(this));
			}
			this._setTabsVisibility();
			this._TabsWidget = null;

			Object.defineProperty(this, 'tabWidget', { get: function() {
				return this._TabsWidget;
			} });
		},

		renderContent: function(){
			if (!this.rendered) {
				return Tab.inherited('renderContent', this, arguments).then(() => {
					this._renderTabs(this.initialTab.id);
					this.showView(this.initialTab);
					this.canvas.selectContent([this.initialTab.id]);
					this.rendered = true;
				});
			} else {
				return Promise.resolve(false);
			}
		},

		destroy: function() {
			this._unregisterEvents();

			ReactDOM.unmountComponentAtNode(this.$el.find('.tabCntr')[0]);
			$('.tabLabel',this.$el).remove();
			Tab.inherited('destroy', this, arguments);
		},

		_registerEvents: function () {
			if (this.colorsService) {
				this.colorsService.on('theme:changed', this.applyTheme, this);
				this.colorsService.on('colorSet:changed', this.applyTheme, this);
			}
			if (this.model) {
				this.model.on('change:tabTextColor', this.applyTheme, this);
				this.model.on('change:tabSelectedLineColor', this.applyTheme, this);
				this.model.on('change:tabBackgroundColor', this.applyTheme, this);
				this.content.on('add:child', this.onTabAdded, this);
				this.content.on('remove:child', this.onTabRemoved, this);
				this.model.on('change:hideTab', this.updateHideTabState, this);
				this.model.on('change:fillColor', this.applyTheme, this);
				this.model.on('change:tabPosition', this.updateTabPosition, this);
				this.model.on('change:tabIconPosition', this.updateTabIconPosition, this);
			}
			if (this.eventRouter) {
				this.eventRouter.on('properties:hasRendered', this.setInitialEnabledState, this);
			}

			this.canvas.on('change:selections:select', this.onSelection, this);
			// TODO: We should modify dashboardstate/callbackinvoker to allow it accept context/'this'
			// so that each consumer doesn't have to maintain the bound function
			this._boundSetTabsVisibility = this._setTabsVisibility.bind(this);
			this._dashboardState.onUiStateChange(this._boundSetTabsVisibility);
		},

		_unregisterEvents: function () {
			if (this.colorsService) {
				this.colorsService.off('theme:changed', this.applyTheme, this);
				this.colorsService.off('colorSet:changed', this.applyTheme, this);
			}

			if (this.model) {
				this.model.off('change:tabTextColor', this.applyTheme, this);
				this.model.off('change:tabSelectedLineColor', this.applyTheme, this);
				this.model.off('change:tabBackgroundColor', this.applyTheme, this);
				this.content.off('add:child', this.onTabAdded, this);
				this.content.off('remove:child', this.onTabRemoved, this);
				this.model.off('change:hideTab', this.updateHideTabState, this);
				this.model.off('change:fillColor', this.applyTheme, this);
				this.model.off('change:tabPosition', this.updateTabPosition, this);
				this.model.off('change:tabIconPosition', this.updateTabIconPosition, this);
			}
			if (this.eventRouter) {
				this.eventRouter.off('properties:hasRendered', this.setInitialEnabledState, this);
			}

			this.canvas.off('change:selections:select', this.onSelection, this);
			this._dashboardState.offUiStateChange(this._boundSetTabsVisibility);
		},

		_getPanelId: function(id) {
			return id + '_tabpanel';
		},

		_getModelIdFromPanel: function(id) {
			return id.slice(0, -9);
		},

		_getDefaultLinePostion: function(tabPosition = 'top') {
			return linePositionMap[tabPosition];
		},

		_getDashboardColorProperties: function () {
			let properties = {};

			colorPropertiesMapping.forEach((property) => {
				_.extend(properties, this._getColorProperty(this.model, property));
			});

			return properties;
		},

		_getColorProperty: function (model, property) {
			const name = property.modelPropertyName;
			const properties = {};
			const bgColorId = model.get('tabBackgroundColor') || model.get('fillColor');
			const themeColors = this.colorsService.getForegroundColorPropertiesForUIElement(bgColorId, 'tabs');

			let hexColor = null;
			if (property && model.get(name)) {
				hexColor =  this.colorsService.getHexColorFromDashboardColorSet(model.get(name));
			}
			if(!hexColor && property.modelPropertyName === 'tabBackgroundColor') {
				hexColor =  this.colorsService.getHexColorFromDashboardColorSet(bgColorId);
			}

			if (property && hexColor) {
				properties[property.toolkitPropertyName] = hexColor;
			} else if (property) {
				const themeColor = _.find(themeColors, (color) => {
					return color.id === name;
				});
				if (themeColor) {
					properties[property.toolkitPropertyName] = themeColor.value;
				}
			}

			return properties;
		},

		_getColorProperties: function(model) {
			let colorProperties = this._getDashboardColorProperties();

			if(model) {
				let changedPageColorPropertyList = this._getChangedPageColorPropertyList(model);
				if(changedPageColorPropertyList.length > 0) {
					const containsTabBackgroundColor = (properties) => {
						return properties.find(property => property.modelPropertyName === 'tabBackgroundColor');
					};
					if(containsTabBackgroundColor(changedPageColorPropertyList)) {
						// The page level tab background is set.
						// This should affect the other properties if they are transparent.
						changedPageColorPropertyList = colorPropertiesMapping;
					}
					changedPageColorPropertyList.forEach(property => {
						const pageTabProperty = this._getColorProperty(model, property);
						_.extend(colorProperties, pageTabProperty);
					});
				}
				// Set the iconColor to the textColor if not defined
				if (!model.get('tabIconColor')) {
					colorProperties['iconColor'] = colorProperties['textColor'];
				}
			}

			this._setDeleteColors(colorProperties);

			return colorProperties;
		},

		_setDeleteColors(properties) {
			let backgroundColor = properties.backgroundColor;
			if(backgroundColor === 'transparent') {
				backgroundColor = this.colorsService.getValueForPropertyOfUIElement('board', 'backgroundColor');
			}
			if(backgroundColor) {
				properties.hoverColor = ColorUtils.invertColor(backgroundColor, 20, 100, true);
				properties.deleteBackgroundColor = ColorUtils.invertColor(backgroundColor, 40, 100, true);
				properties.deleteIconColor = properties.textColor;
			}
		},

		_createTabPanel: function(pageModel, pageContent, tabColorProperties) {
			const TabPanel = UI_Toolkit.TabPanel;
			const _tabIconObj = pageModel.get('tabIcon');
			let tabIcon = _tabIconObj && _tabIconObj !== 'transparent' ? this._icons.getIcon(_tabIconObj).id : null;

			let properties = {
				id: this._getPanelId(pageModel.id),
				label: pageModel.get('title'),
				icon: tabIcon,
				keepTabContent: true,
				tooltipOrient: 'top'
			};

			_.extend(properties, tabColorProperties);

			return React.createElement(TabPanel, properties, [
				React.createElement('div', { ref: (node) => {
					if (node) {
						$(node).append(pageContent);
					} else {
						this.$el.find('.tabPageHolder').append(pageContent);
					}
				} })
			]);
		},

		_renderTabs: function(selectedId) {
			const Tabs = UI_Toolkit.Tabs;
			let panels = [],
				panelsMirror = []; //Mirror entry of the tabs that we can compare with for tab changes.

			this.model.items.forEach((item) => {
				const colorProperties = this._getColorProperties(item);
				const pageNode = this.$el.find('#'+item.id+'_tab');
				const tabPanel = this._createTabPanel(item, pageNode, colorProperties);
				panels.push(tabPanel);
				panelsMirror.push(tabPanel);
			});

			const tabPosition = this.model.get('tabPosition');
			const tabIconPosition = this.model.get('tabIconPosition');
			const _this = this;
			class TabsWidget extends React.Component{
				constructor(props){
					super(props);
					this.state = {
						selectedTab: _this._getPanelId(selectedId),
						tabs: panels,
						editable: false,
						vertical: true,
						draggable: false,
						addable: false,
						deleteable: false,
						placement: tabPosition || 'top',
						linePosition: _this._getDefaultLinePostion(tabPosition),
						tabIconPosition
					};
					this._tabsMirror = panelsMirror;
					this._icons = _this._icons;
					this.tabListNode = null;
					this.toolBarElementsList = [];
				}

				componentDidUpdate(prevProps, prevState) {
					if (this._updatedTabs) {
						this._updatedTabs = false;
						if (this.state.tabs.length < this._tabsMirror.length) { //Delete
							_this.deleteView(_this._getModelIdFromPanel(_.difference(this._tabsMirror, this.state.tabs)[0].props.id));
						}
					}
					this._tabsMirror = this.state.tabs.slice();
					if (prevState.selectedTab !== this.state.selectedTab) {
						_this.showView(_this.model.findModel(_this._getModelIdFromPanel(this.state.selectedTab)));
					}
				}

				componentDidMount() {
					const tabList = _this.$el.find('.ba-common-tabList');
					tabList.on('click.dbTab', _this._tabClick.bind(_this));
					tabList.on('keydown.dbTab', _this._tabKeydown.bind(_this));
					tabList.on('dblclick.dbTab', _this._tabDoubleClick.bind(_this));
					this.tabListNode = tabList;
				}

				componentWillUnmount() {
					const tabList = _this.$el.find('.ba-common-tabList');
					tabList.off('click.dbTab');
					tabList.off('keydown.dbTab');
					tabList.off('dblckick.dbTab');
					this.tabListNode = null;
					this.hasUnmounted = true;
				}

				updateTheme() {
					let newTabs = [];
					for (let i=0; i<this.state.tabs.length; i++) {
						const pageModel = _this.model.items[i];
						const colorProperties = _this._getColorProperties(pageModel);
						newTabs.push(React.cloneElement(this.state.tabs[i], colorProperties));
					}
					this.setState({ tabs: newTabs });
				}

				editTabLabel(startEdit, model) {
					const isEditable = !!startEdit;
					let $node;
					if (this.tabListNode) {
						$node = $(this.tabListNode);
					} else {
						$node = _this.$el.find('.ba-common-tabList');
					}
					$node.find('[role=tab][aria-selected=true]').focus(); //HACK - Move the focus to the tab. If the focus happens to be on the label when editing is turned on, the re-render may happen before we try to focus.

					this.setState({ editable: isEditable }, () => {
						if (isEditable) {
							let label = $node.find('[contentEditable=true]');
							const panelId = _this._getPanelId(model.id);
							let originalLabel;
							const originalTab = _.find(this.state.tabs, panel => (panelId === panel.props.id));
							if (originalTab) {
								originalLabel = originalTab.props.label;
							}
							const observer = new MutationObserver(() => { //HACK HACK HACK HACK ------- Currently, on the first focus to the editable area, the toolkit tabs are re-rendering, thus blurring the focus immediately. The field needs to be found again and re-focused after the re-render (As there is a new label after this) to actually edit.
								if (!$.contains(_this.$el, label)) {
									observer.disconnect(); //The re-render has happened, so we no longer need this observer.
									label = $node.find('[contentEditable=true]');

									// TEMPORARY CODE - Replace when the tabs provide a callback when editing is complete
									label.on('blur.dashboardtabs', () => {
										label.off('blur.dashboardtabs');
										this.setState({ editable: false }, () => {
											const newTab = _.find(this.state.tabs, panel => (panelId === panel.props.id));
											if (newTab) {
												const newLabel = newTab.props.label;
												if (originalLabel !== newLabel) {
													_this.updateViewLabel(model, newLabel);
												}
											}
										});
									});

									label.focus();
								}
							});
							observer.observe(_this.$el[0], { childList: true, subtree: true }); //Observe changes in descendant nodes of the tabs to catch the re-render caused by the first focus.

							label.focus();
						}
					});
				}

				updateTabLabel(model) {
					let newTabs = [], changeMade = false;
					const panelId = _this._getPanelId(model.id);
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						if (tab.props.id == panelId && tab.props.label !== model.get('title')) {
							newTabs.push(React.cloneElement(tab, {
								label: model.get('title')
							}));
							changeMade = true;
						} else {
							newTabs.push(tab);
						}
					}
					if (changeMade) {
						this.setState({ tabs: newTabs });
					}
				}

				updateTabIcon(model) {
					let newTabs = [], changeMade = false;
					const panelId = _this._getPanelId(model.id);
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						const _tabIconObj = model.get('tabIcon');

						let icon;
						if(_tabIconObj && _tabIconObj !== 'transparent') {
							icon = this._icons.getIcon(_tabIconObj).id;
						} else {
							icon = null;
						}

						if(tab.props.id === panelId && tab.props.icon !== icon && !this.translationMode) {
							newTabs.push(React.cloneElement(tab, {
								icon: icon
							}));
							changeMade = true;
						} else {
							newTabs.push(tab);
						}
					}
					if (changeMade) {
						this.setState({ tabs: newTabs });
					}
				}

				/**
				 * @param  {Object} model: the model of a view whose children element need to be translated
				 * @param  {Boolean} shown: when it is true means we want to show the translate icon for this model.
				 * @returns {Int} numberOfChanges: the number of tabs we've updated.
				 */
				updateTranslationIcon(model, shown) {
					const icons = _this.dashboardApi.getFeature('Icons');
					this.translationMode = shown;
					let newTabs = [], changeMade = false, numberOfChanges = 0;
					const panelId = _this._getPanelId(model.id);
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						if (tab.props.id == panelId) {
							if (shown) {
								newTabs.push(React.cloneElement(tab, {
									icon: icons.getIcon('warning').id,
									className: 'translateIcon'
								}));
								changeMade = true;
								numberOfChanges++;
							} else if (tab.props.icon) {
								const tabIcon = model.get('tabIcon');
								newTabs.push(React.cloneElement(tab, {
									icon: tabIcon ? tabIcon.name : undefined,
									className: undefined
								}));
								changeMade = true;
								numberOfChanges++;
							}
						} else {
							newTabs.push(tab);
						}
					}
					if (changeMade) {
						this.setState({ tabs: newTabs });
					}
					return numberOfChanges;
				}

				render() {
					const tabColorProperties = _this._getColorProperties();
					let tabs = this.state.tabs;
					tabs = tabs.map(tab => {
						return React.cloneElement(tab, {
							iconPosition: this.state.tabIconPosition
						});
					});
					_this._TabsWidget = this;
					return React.createElement(
						'div',
						{
							className: 'ba-theme-default',
							style: {
							}
						},
						[
							React.createElement(Tabs, {
								draggable: this.state.draggable,
								selected: this.state.selectedTab,
								stateTabs: this.state.tabs,
								editable: this.state.editable,
								deleteable: this.state.deleteable,
								addable: this.state.addable,
								textDir: BidiUtil.userPreferredTextDir,
								onChange: tabId => {
									const newTabId = _this._getModelIdFromPanel(tabId);
									_this.canvas.selectContent([newTabId]);
								},
								setState: option => { this._updatedTabs = true; this.setState({ tabs: option }); },
								onAddButtonClick: () => _this.onAddClick(),
								onReorder: (newIndex, tabId) => { _this.onReorder(tabs, newIndex, tabId); },
								scrollButtonsBackgroundColor: _this.colorsService.getLayoutBackgroundColorHex(_this.model.get('fillColor')),
								scrollButtonsTextColor: tabColorProperties.textColor,
								onDeletePrompt: _this.onDeletePrompt,
								placement: this.state.placement,
								linePosition: this.state.linePosition
							}, tabs)
						]
					);

				}
			}
			const DragEnabledTabsWidget = ReactHtml5DnDContext(TabsWidget);

			ReactDOM.render(React.createElement(DragEnabledTabsWidget), this.$el.find('.tabCntr')[0]);
		},

		/* Fetch the TabPane object given the TabList tab node.
		 */
		_getPanelFromNode: function(tabNode) {
			if (tabNode) {
				const tabIndex = this.$el.find('.ba-common-tabList [role=tab]').index(tabNode);
				if (tabIndex>-1) {
					return this._TabsWidget.state.tabs[tabIndex];
				}
			}
			return null;
		},

		/**
		 * Fetch the model and tab node to send events from the overall tab control to the appropriate tab.
		 * Returns undefined if the event doesn't map to a tab.
		 */
		_getEventModelInfo: function(event) {
			let result = undefined;

			const $target = $(event.target);
			const tabElement = $target.closest('[role=tab]')[0];
			const isClose = $target.is('svg.is-clickable') && $target.closest('span').hasClass('close');
			if (tabElement && !isClose) {
				const tabPanel = this._getPanelFromNode(tabElement);
				if (tabPanel) {
					const panelId = tabPanel.props.id;
					const isSelected = this._TabsWidget.state.selectedTab === panelId;
					const pageModel = this.model.findModel(this._getModelIdFromPanel(panelId));

					result = {
						selected: isSelected,
						node: tabElement,
						model: pageModel,
						editing: () => this._TabsWidget.state.editable
					};
				}
			}
			return result;
		},

		_tabClick: function(event) {
			//this prevents the context bar from appearing when showing the tab toolbar
			let eventChainLocal = new EventChainLocal(event);
			eventChainLocal.setProperty('preventDefaultContextBar', true);

			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected && !eventInfo.editing()) {
				//This click could have triggered a tab delete. To avoid relying on additional internal information about the tabs, add a short delay and then check if the node is still in the DOM before trying to trigger the toolbar.
				setTimeout(() => {
					if ($(eventInfo.node).closest('.ba-common-tabList').length && !eventInfo.editing()) {
						this.showToolbar(eventInfo.model, eventInfo.node);
					}
				}, 10);
			}
		},

		_tabKeydown: function(event) {
			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected) {
				this._handleKeydown(eventInfo.model, event, eventInfo.node);
			}
		},

		_tabDoubleClick: function(event) {
			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected && !eventInfo.editing()) {
				//Mirror the toolbar show timeout to prevent the toolbar showing from immediately bluring the text field when edit starts. Additionally check that the delete button was not the target of the doubleclick.
				setTimeout(() => {
					if ($(eventInfo.node).closest('.ba-common-tabList').length) {
						this.editViewTitle(eventInfo.model);
					}
				}, 50);
			}
		},

		_attachView: function(/*model*/) {
			//Overridden in authoring.
		},

		onAddClick: function() {
			//Overrridden in authoring.
		},

		onReorder: function(/*tabsList, newIndex, tabId*/) {
			//Overridden in authoring.
		},

		onDeletePrompt: function(deleteCalbackFn) {
			var dlg = new ConfirmationDialog('none', StringResources.get('tabDeleteDialogTitle'), StringResources.get('tabDeleteDialogMessage'));
			dlg.setDialogOptions({
				buttons: [{
					'text': StringResources.get('tabDeleteDialogNoButton'),
					'handler': dlg.cancel,
					'type': 'primary',
					'defaultId': 'no_button'
				},
				{
					'text': StringResources.get('tabDeleteDialogYesButton'),
					'handler': dlg.ok,
					'type': 'secondary',
					'defaultId': 'yes_button'
				}]
			});
			dlg.confirm(deleteCalbackFn);
		},

		deleteView: function(/* sViewId */) {
			//Overridden in authoring.
		},

		updateViewLabel: function(/*pageModel, sText*/) {
			//Overridden in authoring.
		},

		getSelectedSubViewId: function() {
			return this.selectedModel.id;
		},

		onSelection: function(event) {
			event.info.value.forEach((id) => {
				const canvas = this.canvas;
				const content = canvas.getContent(id);
				if(content && content.getType() === 'page') {
					if(this.selectedModel && this.selectedModel.id !== id) {
						canvas.deselectContent([this.selectedModel.id]);
					}
					this._TabsWidget.setState({ selectedTab: this._getPanelId(id) });
				}
			});
		},

		getSelectedSubViewTitle: function() {
			return this.selectedModel.get('title');
		},

		showView: function(model) {
			const sTabId = this._getViewId(model);
			if (this._currentViewId === sTabId) {
				return;
			}

			if (this.selectedModel) {
				const layoutView = this.layoutController.getLayoutView(this.selectedModel.id);
				if (layoutView) {
					layoutView.onHide();
				}
			}

			this.$el.find('.pageTabContent.selected').removeClass('selected');
			$('#' + sTabId, this.$el).addClass('selected');

			this._currentViewId = sTabId;
			this.selectedModel = model;

			this._TabsWidget.setState({ selectedTab: this._getPanelId(this.selectedModel.id) });
			this.eventRouter.trigger('tab:tabChanged', { modelId: this.selectedModel.id });

			return this.layoutController.getLayoutViewWhenReady(this.selectedModel.id)
				.then(function(layoutView) {
					layoutView.onShow();
					return this._invokeLifeCycleHandlers('tabs.onShow', {
						$el: this.$el,
						model: model
					}).then(function () {
						//default behavior goes in here.
					}.bind(this));
				}.bind(this));
		},

		_getLabelId: function(contentModel) {
			return contentModel.id + '_tablabel';
		},

		_handleKeydown: function(/*model, event, node*/) {
		},

		showToolbar: function(/*model, node*/) {
			//Overridden in authoring.
		},

		editViewTitle: function(/*model*/) {
			//Overridden in authoring.
		},

		/**
		 * Helper to delegate to this.showView
		 * this double delegation is needed as this.showView may get overridden when authoring
		 * When moving to author mode, event handlers using 'this.showView()' in _attachView() in consume view ctor
		 * need to invoke the overridden showView()
		 *
		 * PL DO NOT REMOVE THIS FUNCTION
		 */
		_showView: function(model) {
			this.showView(model);
		},

		_getViewId: function(contentModel) {
			return contentModel.id + '_tab';
		},

		_invokeLifeCycleHandlers: function(name, payload) {
			return this.dashboardApi.getDashboardSvc('.LifeCycleManager')
				.then(function (LifeCycleManager) {
					return LifeCycleManager.invokeLifeCycleHandlers(name, payload);
				});
		},

		applyTheme: function() {
			this._TabsWidget.updateTheme();
		},

		onTabAdded: function(payload) {
			this._updateHideTabProperty();
			const state = this.canvas.getContent(payload.info.newContentId).getFeature('state');
			return state.whenStatusChanges(state.STATUS.RENDERED).then(() => {
				this._TabsWidget.updateTheme();
				this.eventRouter.trigger('properties:refreshPane');
			});
		},

		onTabRemoved: function() {
			this._updateHideTabProperty();
			this.eventRouter.trigger('properties:refreshPane');
		},

		_updateHideTabProperty: function() {
			const allowHidingMultipleTabs = this.content.getPropertyValue('allowHidingMultipleTabs');
			if (!allowHidingMultipleTabs) {
				const haveMultipleTabs = this.model.items.length > 1;
				this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'showTabsInPreview', enabled: !haveMultipleTabs });
				if (haveMultipleTabs) {
					this.content.setPropertyValue('showTabsInPreview', true);
				}
			}
		},

		updateHideTabState: function() {
			const checked = !this.model.get('hideTab');
			this.eventRouter.trigger('properties:setValue', { propertyName: 'showTabsInPreview', value: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabIconPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabTextColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabSelectedLineColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabBackgroundColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabRestoreDefaults', enabled: checked });
			this._setTabsVisibility();
		},

		updateTabPosition: function(options) {
			if(this._TabsWidget && options) {
				const tabPosition = options.value || 'top';
				this._TabsWidget.setState({
					placement: tabPosition,
					linePosition: this._getDefaultLinePostion(tabPosition)
				});
				const updatePayload = {
					propertyName: 'tabPosition',
					value: tabPosition
				};
				this.eventRouter.trigger('properties:setValue', updatePayload);
			}
		},

		updateTabIconPosition: function(options) {
			if(this._TabsWidget && options && options.value) {
				const tabIconPosition = options.value;
				this._TabsWidget.setState({
					tabIconPosition
				});
			}
		},

		setInitialEnabledState: function() {
			const checked = !this.model.get('hideTab');
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabIconPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabTextColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabSelectedLineColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabBackgroundColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabRestoreDefaults', enabled: checked });
		},

		updateState: function (state) {
			if (!this._TabsWidget.hasUnmounted) {
				//This is the case where in authouring mode and the user decide to close the dahsboard from the switcher

				//Should not be calling consume's view React set state here since the consumer view's destroy unmount the React component
				//Call setState will cause the error in the console:
				//Warning: Can't call setState (or forceUpdate) on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in the componentWillUnmount method.
				//in TabsWidget (created by DragDropContext(TabsWidget))
				//in DragDropContext(TabsWidget)
				this._TabsWidget.setState(state);
			}
		},

		_onTabColorChange: function (propertyName, propertyValueInfo) {
			// get layout and update CSS
			var oLayout = this.model;
			var data = {
				undoRedoTransactionId: _.uniqueId(`layout_${propertyName}Changed_`)
			};

			const payload = {};
			// Treat transparent as reset to default
			payload[propertyName] = propertyValueInfo.name == 'transparent' ? null : propertyValueInfo.name;
			this.colorsService.prepareForColorModelChange(payload, propertyName);

			oLayout.set(payload, {
				sender: this.senderId,
				payloadData: data
			});
		},

		_getChangedPageColorPropertyList: function (model) {
			return colorPropertiesMapping.filter(property => model.get(property.modelPropertyName));
		},

		_setTabsVisibility: function() {
			const hideTabs = !this.content.getPropertyValue('showTabsInPreview') && !this._dashboardState.getUiState().authoring;
			this.$el.toggleClass('tabsHidden', hideTabs);
		}
	});

	return Tab;
});
